import { useCallback, useEffect } from 'react'

import { ColDef, Column, GridOptions, ICellRendererParams, RowModelType } from 'ag-grid-community'

import { isNotEqual } from '@utils/lodash'

import { CHANGE_KEY } from '@hooks/use-change-detection'

import { LogGroup, logError } from '@store/actions/log'
import { useDispatch } from '@store/index'

import { AgGridRef } from 'contexts/grid-ref-context'

import {
  GrandTotalSettings,
  PivotRowTotalsType,
  isGrandTotalRowPinned,
  isGrandTotalRowTop
} from '../../types/table-builder-types'

const GRAND_TOTAL_HEADER_NAME = 'Grand Total'
const PIVOT_ROW_TOTAL_PREFIX = 'PivotRowTotal_pivot_' // This is auto-generated by ag-grid to identify pivot row totals
export const isPivotRowTotal = (colId: string) => _.startsWith(colId, PIVOT_ROW_TOTAL_PREFIX)
export const isColumnPivotRowTotal = (column: Column) => isPivotRowTotal(column.getColId())

type GrandTotalProps = GrandTotalSettings & {
  gridRef: AgGridRef
  events: {
    firstDataRendered: CHANGE_KEY
    externalFiltersChanged: CHANGE_KEY
  }
}

export function useGrandTotals({
  pivotRowTotals,
  showTotalFooter,
  grandTotalRow,
  groupTotalRow,
  gridRef,
  events
}: GrandTotalProps) {
  const dispatch = useDispatch()
  const getGrandTotalsProps = () => {
    const gridOptions: Partial<
      Pick<
        GridOptions,
        | 'pivotRowTotals'
        | 'processPivotResultColDef'
        | 'suppressAggFuncInHeader'
        | 'grandTotalRow'
        | 'groupTotalRow'
      >
    > = {}

    if (pivotRowTotals && isNotEqual(pivotRowTotals, PivotRowTotalsType.NONE)) {
      gridOptions.pivotRowTotals = pivotRowTotals as GridOptions['pivotRowTotals']
      gridOptions.processPivotResultColDef = (colDef) => {
        if (_.isEqual(_.size(colDef.pivotKeys), 0)) {
          colDef.headerName = GRAND_TOTAL_HEADER_NAME
        }
      }
      gridOptions.suppressAggFuncInHeader = true
    }

    if (showTotalFooter && !isGrandTotalRowPinned(grandTotalRow)) {
      if (isGrandTotalRowTop(grandTotalRow)) {
        gridOptions.grandTotalRow = 'top'
      } else {
        gridOptions.grandTotalRow = 'bottom'
      }
    }

    gridOptions.groupTotalRow = groupTotalRow

    return gridOptions
  }

  const getAutoGroupColDefCellRendererParams = useCallback(() => {
    const cellRendererParams: Partial<ColDef['cellRendererParams']> = {}

    if (showTotalFooter) {
      cellRendererParams.totalValueGetter = (params: ICellRendererParams) => {
        const isRootLevel = params.node.level === -1
        if (isRootLevel) {
          return GRAND_TOTAL_HEADER_NAME
        }
        return `Total ${params.value}`
      }
    }
    return cellRendererParams
  }, [showTotalFooter])

  useEffect(() => {
    const gridApi = gridRef?.current?.api
    if (!gridApi || !showTotalFooter || !isGrandTotalRowPinned(grandTotalRow)) return

    try {
      const rowGroupColumns = _.first(_.invoke(gridApi, 'getRowGroupColumns', []))
      const rowGroupColId = _.invoke(rowGroupColumns, 'getColId', '')
      // TODO: Fix this gridApi.getModel() is deprecated
      const model = gridApi.getGridOption('rowModelType') as RowModelType
      gridApi.setGridOption('pinnedBottomRowData', [
        _.assign(
          {},
          Reflect.get(model as unknown as object, 'rootNode').aggData,
          rowGroupColId && { [rowGroupColId]: { headerName: GRAND_TOTAL_HEADER_NAME } }
        )
      ])
    } catch (error) {
      // Applying aggregate data from private ag-grid variable. Mentioned here: https://github.com/ag-grid/ag-grid/issues/1791#issuecomment-1651832960
      // Adding try-catch because this is a private API and can change in future versions
      dispatch(
        logError({
          group: LogGroup.AG_GRID_INTERNAL_CHANGE,
          message: 'Error while setting pinnedBottomRowData',
          tags: {
            type: 'grandTotalFooterPinned',
            error: (error as Error)?.message || 'Unknown error'
          }
        })
      )
    }
  }, [
    gridRef,
    grandTotalRow,
    showTotalFooter,
    events.firstDataRendered,
    events.externalFiltersChanged,
    dispatch
  ])

  return {
    getGrandTotalsProps,
    getAutoGroupColDefCellRendererParams
  }
}
